// Debounces search input for contact list search
import { useEffect, useState } from "react"

/**
 * Custom React hook to debounce a changing value.
 *
 * This hook delays updating the returned value until after the specified
 * delay has passed without the input value changing.
 *
 * Common use cases:
 * - Debouncing search input values
 * - Preventing excessive API calls
 * - Optimizing performance for frequently changing state
 *
 * @template T
 * Generic type of the value being debounced.
 *
 * @param {T} value
 * The value to debounce.
 *
 * @param {number} delay
 * Delay in milliseconds before the debounced value updates.
 *
 * @returns {T}
 * The debounced value, updated only after the delay has elapsed.
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}